import { useCallback } from 'react';
import type { PhieuXuatDieuChuyen, PhieuXuatDieuChuyenSearchFilters } from '@/types/schemas';
import { getDataTableColumns } from '../cols-definition';
import { SearchFormValues } from '../schema';
import { useDynamicSearch } from '@/hooks';
import { QUERY_KEYS } from '@/constants';

interface UseSearchTableProps {
  searchData: (filters: PhieuXuatDieuChuyenSearchFilters, page?: number, searchPageSize?: number) => Promise<void>;
  searchParams: SearchFormValues;
  setSearchParams: (params: SearchFormValues) => void;
  loading: boolean;
  handleViewClick: (obj: PhieuXuatDieuChuyen) => void;
  sumTotal: number | null;
  isFiltered: boolean;
  handlePageChange: (page: number) => Promise<void>;
}

export const useSearchTable = ({
  searchData,
  searchParams,
  setSearchParams,
  loading,
  handleViewClick,
  sumTotal,
  isFiltered,
  handlePageChange
}: UseSearchTableProps) => {
  const onSearch = useCallback(
    async (params: PhieuXuatDieuChuyenSearchFilters) => {
      await searchData(params);
    },
    [searchData]
  );

  const onInitialSearch = useCallback(
    async (
      formData: SearchFormValues & { dynamic_filters?: Record<string, unknown> },
      onSearchFn: (params: PhieuXuatDieuChuyenSearchFilters) => Promise<void>
    ) => {
      const { dynamic_filters, ...searchParamsOnly } = formData;
      setSearchParams(searchParamsOnly as SearchFormValues);
      await onSearchFn(formData);
    },
    [setSearchParams]
  );

  const getColumns = useCallback(() => {
    const cols = getDataTableColumns(handleViewClick, sumTotal, loading);
    return cols.map(col => ({
      field: col.field,
      type: col.type
    }));
  }, [handleViewClick, sumTotal, loading]);

  const {
    searchState,
    handleSearchChange,
    handleSearchPageChange,
    hasFilterParams,
    handleInitialSearch,
    handleRefreshClick,
    isSearching
  } = useDynamicSearch({
    onSearch,
    params: searchParams,
    onInitialSearch,
    getColumns,
    endpoint: `${QUERY_KEYS.PHIEU_XUAT_DIEU_CHUYEN}/filter`,
    pageSize: 100
  });

  const onPageChange = useCallback(
    async (page: number) => {
      if (hasFilterParams) {
        await handleSearchPageChange(page);
      } else if (isFiltered) {
        await searchData(searchParams, page);
      } else {
        await handlePageChange(page);
      }
    },
    [hasFilterParams, handleSearchPageChange, isFiltered, searchParams, searchData, handlePageChange]
  );

  return {
    searchState,
    handleSearchChange,
    onPageChange,
    hasFilterParams,
    handleInitialSearch,
    handleRefreshClick,
    isFiltering: isSearching
  };
};
